import type { IconName } from '@/components/icons/types';
import type { JourneyTimelineEvent } from '@/types/verification';

/**
 * Pure grouping/presentation logic for the guardian journey timeline, kept out
 * of the React component so it can be unit-tested without a DOM
 * (test/journey-timeline.test.ts).
 */

export interface JourneyDayGroup {
  /** Local-calendar day key, YYYY-MM-DD — grouping follows the viewer's clock. */
  dayKey: string;
  /** Midnight (local) of the day, for locale-aware heading formatting. */
  date: Date;
  /** Events of that day, newest first (the server order, preserved). */
  events: JourneyTimelineEvent[];
}

/** The viewer's local day of a timestamp, as a stable sortable key. */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Groups a newest-first event list into newest-first day buckets.
 *
 * Events whose timestamp cannot be parsed are dropped rather than piled into a
 * garbage bucket: a corrupt row must not invent a day in a parent's view of
 * their child. Input order within a day is preserved; days themselves are
 * sorted by key descending so an out-of-order server response still renders
 * chronologically.
 */
export function groupEventsByDay(events: readonly JourneyTimelineEvent[]): JourneyDayGroup[] {
  const buckets = new Map<string, JourneyTimelineEvent[]>();
  for (const event of events) {
    const key = localDayKey(event.occurredAt);
    if (!key) continue;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(event);
    else buckets.set(key, [event]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([dayKey, dayEvents]) => {
      const [year, month, day] = dayKey.split('-').map(Number);
      return { dayKey, date: new Date(year, month - 1, day), events: dayEvents };
    });
}

/** True when the key names the viewer's current local day. */
export function isTodayKey(dayKey: string, now: Date = new Date()): boolean {
  return dayKey === localDayKey(now.toISOString());
}

/**
 * The icon shown beside each event, drawn from the app's existing icon set (no
 * bus or library glyph exists, so the nearest honest neighbour is used). Kept
 * as a data map (not a switch in JSX) so an unknown/new server event type falls
 * back to a neutral dot instead of crashing — the label does the same via the
 * i18n raw-code fallback.
 */
export const JOURNEY_EVENT_ICONS: Record<string, IconName> = {
  ARRIVAL: 'campus',
  CLASS_ATTENDANCE: 'academic',
  LIBRARY: 'report',
  BUS_BOARDING: 'external',
  BUS_DROPOFF: 'check',
  EXAM_CHECKIN: 'checklist',
  EVENT_ACCESS: 'star',
  IDENTITY_CHECK: 'profile',
};

export const JOURNEY_EVENT_FALLBACK_ICON: IconName = 'dot';
